import Cookies from 'js-cookie';

export async function csrfFetch(url, options = {}, retryCount = 0) {
  options.method = options.method || 'GET';
  options.headers = options.headers || {};
  options.credentials = 'include';

  if (options.method.toUpperCase() !== 'GET') {
    // Don't set Content-Type for FormData (let browser set it with boundary)
    const isFormData = options.body instanceof FormData;

    if (!isFormData) {
      options.headers['Content-Type'] = options.headers['Content-Type'] || 'application/json';
    }

    options.headers['Accept'] = 'application/json';

    let token = Cookies.get('csrf_token');
    if (!token) {
      // If no token exists, try to refresh it first
      await refreshCSRFToken();
      token = Cookies.get('csrf_token');
      if (!token) {
        throw new Error('Unable to obtain CSRF token');
      }
    }
    options.headers['X-CSRFToken'] = token;
  }

  try {
    const res = await fetch(url, options);

    // Check for CSRF token errors in the response
    if (res.status === 403) {
      const data = await res.json().catch(() => ({}));

      // If this is a CSRF token error and we haven't retried too many times
      if (
        (data.error?.toLowerCase().includes('csrf') ||
         data.error?.toLowerCase().includes('token')) &&
        retryCount < 3
      ) {
        console.log('CSRF token error detected, refreshing token and retrying...');

        // Clear the existing token
        Cookies.remove('csrf_token');

        // Refresh the token
        await refreshCSRFToken();

        // Retry the request with the new token
        return csrfFetch(url, options, retryCount + 1);
      }
    }

    // Check if the response is ok (status in the range 200-299)
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.errors?.[0] || 'An error occurred');
    }

    return res;
  } catch (error) {
    console.error('csrfFetch error:', error);
    throw error;
  }
}

// Function to check if an error is related to CSRF token
export const isCSRFTokenError = (error) => {
  if (!error) return false;

  // Check error message for CSRF related terms
  if (error.message) {
    const message = error.message.toLowerCase();
    return (
      message.includes('csrf') ||
      message.includes('token') ||
      message.includes('invalid csrf') ||
      message.includes('forbidden')
    );
  }

  // Check if the error response status is 403 (common for CSRF errors)
  if (error.status === 403) {
    return true;
  }

  return false;
};

// Function to refresh the CSRF token
export const refreshCSRFToken = async () => {
  try {
    console.log('Refreshing CSRF token...');

    // Make a GET request to the CSRF restore endpoint
    const response = await fetch('/api/csrf/restore', {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error('Failed to refresh CSRF token');
    }

    // Wait a short time to ensure the cookie is set
    await new Promise(resolve => setTimeout(resolve, 100));

    // Verify that we got a new token
    const newToken = Cookies.get('csrf_token');
    if (!newToken) {
      console.error('No CSRF token in cookies after refresh');
      // Try to get token from response cookies
      const cookies = document.cookie.split(';');
      const csrfCookie = cookies.find(cookie => cookie.trim().startsWith('csrf_token='));
      if (csrfCookie) {
        const token = csrfCookie.split('=')[1];
        Cookies.set('csrf_token', token);
        return true;
      }
      throw new Error('No CSRF token received after refresh');
    }

    console.log('CSRF token refreshed successfully');
    return true;
  } catch (error) {
    console.error('Error refreshing CSRF token:', error);
    throw error;
  }
};
